import { useCallback } from 'react';
import { Edge, EdgeChange, EdgeRemoveChange } from '@xyflow/react';

// Define EdgeSelectionChange type for filtering edge selection changes
interface EdgeSelectionChange {
  type: 'select';
  id: string;
  selected: boolean;
}

/**
 * Custom hook for handling edge changes in React Flow dependency maps
 *
 * This hook manages two types of edge changes:
 * 1. Selection changes - handles single edge selection/deselection
 * 2. Remove changes - handles edge deletion and dependency removal
 *
 * Key behaviors:
 * - **Single Selection**: Only one edge can be selected at a time
 * - **Deselection**: Selecting a new edge deselects previous ones
 * - **Removal**: Deleted edges trigger dependency removal via callback
 * - **Change Filtering**: Processes only relevant change types
 *
 * Selection Logic:
 * - If edge is being selected → make it the only selected edge
 * - If edge is being deselected → clear all selections
 * - Selection state is managed via setSelectedEdges callback
 *
 * Removal Logic:
 * - Filters EdgeChange[] for remove operations
 * - Extracts edge IDs from removal changes
 * - Calls removeDependenciesByIds to handle dependency cleanup
 *
 * @param edges - Array of current React Flow edges (for finding selected edge objects)
 * @param setSelectedEdges - State setter to update selected edges array
 * @param removeDependenciesByIds - Function to remove dependencies by edge IDs
 * @returns EdgeChange handler function for React Flow onEdgesChange
 */
export function useEdgesChangeHandler(
  edges: Edge[],
  setSelectedEdges: React.Dispatch<React.SetStateAction<Edge[]>>,
  removeDependenciesByIds: (edgeIds: string[]) => void,
) {
  return useCallback(
    (changes: EdgeChange[]) => {
      // Handle selection changes
      const selectionChanges = changes.filter(
        (change): change is EdgeSelectionChange =>
          change.type === 'select' && 'id' in change && 'selected' in change,
      );
      if (selectionChanges.length > 0) {
        setSelectedEdges((prev) => {
          // If the edge is being selected, make it the only selected edge
          const change = selectionChanges[0];
          if (change.selected) {
            const edge = edges.find((e) => e.id === change.id);
            return edge ? [edge] : [];
          }
          // If the edge is being deselected, clear selection
          return [];
        });
      }

      // Handle edge removal
      const removedEdgeIds = changes
        .filter((change): change is EdgeRemoveChange => change.type === 'remove' && 'id' in change)
        .map((change) => change.id);
      if (removedEdgeIds.length > 0) {
        removeDependenciesByIds(removedEdgeIds);
      }
    },
    [edges, setSelectedEdges, removeDependenciesByIds],
  );
}
